import React from 'react';
import { Calendar } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import get from 'lodash/get';

import { TIME_FORMAT } from '../../../../../constants/time';
import { COLOR } from '../../../../../constants/theme';
import {
  CalendarLegend,
  CalendarWrapper,
  DayCell,
  LegendItem,
  LegendSwatch,
  MonthNav,
  MonthNavButton,
  MonthTitle,
} from './styles';
import { generateAppointmentType } from '../../../../../utils/bookingUtils';
import { getCurrentMonth } from '../../../../../utils/timezoneUtils';
import { APPOINTMENT_TYPE } from '../../../../../constants';

const { DAY, MONTH, DATE_WITH_DASH } = TIME_FORMAT;
const MONTH_TITLE_FORMAT = 'MMMM YYYY';

const AppointmentCalendar = ({
  availableTimeList,
  dispatch,
  bookingTime,
  timezone,
}) => {
  const workflow = useSelector(({ workflow }) => workflow.data);
  const dueDate = get(workflow, 'appointment.dueDate', '');
  const appointmentType = generateAppointmentType(
    get(workflow, 'appointment.isNewAppointment', false)
  );

  const availableDateList = availableTimeList.map(
    (availableDate) => availableDate.date
  );

  const isDateAvailable = (currentDate) => {
    const afterReviewDueTime =
      appointmentType !== APPOINTMENT_TYPE.REVIEW || currentDate >= dueDate;

    return (
      availableTimeList?.length > 0 &&
      availableDateList.includes(currentDate) &&
      afterReviewDueTime
    );
  };

  const customRenderDate = (current) => {
    const currentDate = moment(current).format(DATE_WITH_DASH);
    const inTheSameMonth =
      moment(current).month() === moment(bookingTime.month).month();

    if (!inTheSameMonth) return null;

    const isAvailable = isDateAvailable(currentDate);

    return (
      <DayCell
        $isAvailable={isAvailable}
        $isSelected={isAvailable && bookingTime?.date === currentDate}
      >
        {current.format(DAY)}
      </DayCell>
    );
  };

  const shownMonth = moment(bookingTime.month, MONTH);
  // No going back past this month (in the booking's timezone): earlier
  // months have nothing to book.
  const isFirstMonth = bookingTime.month <= getCurrentMonth(timezone);

  const changeMonth = (offset) => {
    dispatch({
      type: 'updateMonth',
      month: shownMonth.clone().add(offset, 'month').format(MONTH),
    });
  };

  // `‹ October 2026 ›` paging in place of antd's year and month selects and
  // Month / Year switch, as in pbt-ui.
  const renderHeader = () => (
    <MonthNav>
      <MonthNavButton
        type="button"
        aria-label="Previous month"
        disabled={isFirstMonth}
        onClick={() => changeMonth(-1)}
      >
        <LeftOutlined />
      </MonthNavButton>
      <MonthTitle aria-live="polite">
        {shownMonth.format(MONTH_TITLE_FORMAT)}
      </MonthTitle>
      <MonthNavButton
        type="button"
        aria-label="Next month"
        onClick={() => changeMonth(1)}
      >
        <RightOutlined />
      </MonthNavButton>
    </MonthNav>
  );

  const handleSelectDate = (date) => {
    const selectedDate = date.format(DATE_WITH_DASH);

    if (isDateAvailable(selectedDate)) {
      dispatch({
        type: 'updateDate',
        date: selectedDate,
      });
    }
  };

  return (
    <>
      <CalendarWrapper>
        <Calendar
          onSelect={handleSelectDate}
          fullscreen={false}
          value={shownMonth}
          dateFullCellRender={customRenderDate}
          headerRender={renderHeader}
        />
      </CalendarWrapper>

      <CalendarLegend>
        <LegendItem>
          <LegendSwatch
            $background={COLOR.primaryLight}
            $borderColor={COLOR.primaryBorder}
          />
          Available
        </LegendItem>
        <LegendItem>
          <LegendSwatch $background={COLOR.primary} />
          Selected
        </LegendItem>
        <LegendItem>
          <LegendSwatch $background={COLOR.mutedBackground} />
          No appointments
        </LegendItem>
      </CalendarLegend>
    </>
  );
};

export default AppointmentCalendar;
